/**
 * KeyMovements — list of verdict shifts (max 5). Each row shows:
 * creative name, "from" badge, arrow, "to" badge.
 */

import { ArrowRight } from 'lucide-react';

import { useVerdictSummary } from '@/hooks/useDashboard';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { VerdictMovement } from '@/types/dashboard';

// ============================================================================
// CONSTANTS
// ============================================================================

const VERDICT_COLORS: Record<string, string> = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function KeyMovements() {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);

  const movements = data?.keyMovements;
  if (!movements || movements.length === 0) return null;

  const displayed = movements.slice(0, 5);
  const improvedCount = displayed.filter((m) => m.to === 'Scale').length;

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle p-3">
      <div className="text-xs font-semibold text-primary mb-1">Key Movements</div>
      <div className="text-xs text-secondary mb-2">
        {improvedCount} creative{improvedCount !== 1 ? 's' : ''} improved to Scale this period
      </div>

      <div className="space-y-2">
        {displayed.map((movement) => (
          <MovementRow key={movement.creativeId} movement={movement} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// ROW
// ============================================================================

function MovementRow({ movement }: { movement: VerdictMovement }) {
  const fromColor = VERDICT_COLORS[movement.from] ?? '#8B95A7';
  const toColor = VERDICT_COLORS[movement.to] ?? '#8B95A7';

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-primary truncate flex-1">{movement.creativeName}</span>
      <div className="flex items-center gap-1 flex-shrink-0">
        <span
          className="text-xs px-1.5 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${fromColor}20`, color: fromColor }}
        >
          {movement.from}
        </span>
        <ArrowRight className="w-3 h-3 text-secondary" />
        <span
          className="text-xs px-1.5 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${toColor}20`, color: toColor }}
        >
          {movement.to}
        </span>
      </div>
    </div>
  );
}
